import React, { useState, useRef } from 'react';
import { FieldValidation } from '../../types';

interface FormFieldProps {
  label: string;
  name: string;
  type: 'text' | 'number' | 'date' | 'file' | 'select' | 'textarea';
  value: any;
  onChange: (value: any) => void;
  error?: string;
  placeholder?: string;
  example?: string;
  validation?: FieldValidation;
  options?: { value: string; label: string }[];
  accept?: string;
  required?: boolean;
  allowEmpty?: boolean;
}

const FormField: React.FC<FormFieldProps> = ({
  label,
  name,
  type,
  value,
  onChange,
  error,
  placeholder,
  example,
  validation,
  options,
  accept,
  required = false,
  allowEmpty = false,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [displayValue, setDisplayValue] = useState(value?.toString() || '');
  const inputRef = useRef<HTMLInputElement>(null);

  // Update display value when external value changes
  React.useEffect(() => {
    if (!isFocused) {
      setDisplayValue(value?.toString() || '');
    }
  }, [value, isFocused]);

  // Native file inputs cannot be controlled; clear the element when the value is removed
  React.useEffect(() => {
    if (type === 'file' && !value && inputRef.current) {
      inputRef.current.value = '';
    }
  }, [type, value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    if (type === 'file') {
      const file = (e.target as HTMLInputElement).files?.[0] || null;
      onChange(file);
    } else if (type === 'number') {
      const inputValue = e.target.value;
      setDisplayValue(inputValue);
      
      // If the field is empty or contains only whitespace, set to empty
      if (inputValue === '' || inputValue.trim() === '') {
        onChange(allowEmpty ? undefined : '');
      } else {
        // Convert to number, but don't default to 0
        const numValue = Number(inputValue);
        if (!isNaN(numValue)) {
          onChange(numValue);
        }
      }
    } else {
      onChange(e.target.value);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    // Clear the field if it contains only "0"
    if (displayValue === '0') {
      setDisplayValue('');
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    // If the field is empty after blur, keep it empty when allowed; otherwise default to 0
    if (displayValue === '' || displayValue.trim() === '') {
      if (allowEmpty) {
        setDisplayValue('');
        onChange(undefined);
      } else {
        setDisplayValue('0');
        onChange(0);
      }
    }
  };

  const handleIncrement = () => {
    const currentValue = Number(displayValue) || 0;
    const newValue = currentValue + 1;
    setDisplayValue(newValue.toString());
    onChange(newValue);
  };

  const handleDecrement = () => {
    const currentValue = Number(displayValue) || 0;
    const floor = validation?.min ?? (allowEmpty ? Number.NEGATIVE_INFINITY : 0);
    const newValue = Math.max(floor, currentValue - 1);
    setDisplayValue(newValue.toString());
    onChange(newValue);
  };

  const renderInput = () => {
    switch (type) {
      case 'textarea':
        return (
          <textarea
            id={name}
            name={name}
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--primary-deep-blue)] ${
              error ? 'border-red-500' : 'border-[var(--neutral-medium-gray)]'
            }`}
            rows={4}
            required={required}
          />
        );
      
      case 'select':
        return (
          <select
            id={name}
            name={name}
            value={value}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--primary-deep-blue)] ${
              error ? 'border-red-500' : 'border-[var(--neutral-medium-gray)]'
            }`}
            required={required}
          >
            <option value="">Select {label}</option>
            {options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      
      case 'file':
        return (
          <div className="space-y-2">
            <input
              ref={inputRef}
              id={name}
              name={name}
              type="file"
              onChange={handleChange}
              accept={accept}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--primary-deep-blue)] ${
                error ? 'border-red-500' : 'border-[var(--neutral-medium-gray)]'
              }`}
              required={required}
            />
            {value instanceof File && (
              <div className="flex items-center justify-between gap-3 rounded-md bg-[var(--secondary-pearl)] px-3 py-2">
                <p className="text-xs text-[var(--neutral-dark-gray)] truncate">
                  {value.name} ({(value.size / 1024 / 1024).toFixed(2)} MB)
                </p>
                <button
                  type="button"
                  onClick={() => onChange(null)}
                  className="text-xs font-medium text-[var(--error)] hover:underline whitespace-nowrap"
                >
                  Remove file
                </button>
              </div>
            )}
          </div>
        );
      
      case 'number':
        return (
          <div className="relative">
            <input
              ref={inputRef}
              id={name}
              name={name}
              type="number"
              value={displayValue}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder={placeholder}
              className={`w-full px-3 py-2 pr-20 border rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--primary-deep-blue)] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                error ? 'border-red-500' : 'border-[var(--neutral-medium-gray)]'
              }`}
              required={required}
              min={validation?.min}
              max={validation?.max}
            />
            <div className="absolute right-1 top-1/2 transform -translate-y-1/2 flex flex-col">
              <button
                type="button"
                onClick={handleIncrement}
                className="w-6 h-4 flex items-center justify-center text-xs bg-[var(--neutral-light-gray)] hover:bg-[var(--neutral-medium-gray)] border border-[var(--neutral-medium-gray)] rounded-t-sm transition-colors"
                aria-label="Increment"
              >
                +
              </button>
              <button
                type="button"
                onClick={handleDecrement}
                className="w-6 h-4 flex items-center justify-center text-xs bg-[var(--neutral-light-gray)] hover:bg-[var(--neutral-medium-gray)] border border-[var(--neutral-medium-gray)] border-t-0 rounded-b-sm transition-colors"
                aria-label="Decrement"
              >
                −
              </button>
            </div>
          </div>
        );
      
      default:
        return (
          <input
            id={name}
            name={name}
            type={type}
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--primary-deep-blue)] ${
              error ? 'border-red-500' : 'border-[var(--neutral-medium-gray)]'
            }`}
            required={required}
          />
        );
    }
  };

  return (
    <div className="grid gap-2">
      <label htmlFor={name} className="body-small font-medium text-[var(--neutral-dark-gray)]">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      {renderInput()}
      
      {example && (
        <p className="text-xs text-[var(--secondary-slate)] italic">
          Example: {example}
        </p>
      )}
      
      {error && (
        <div className="space-y-1">
          <p className="text-xs text-red-500">
            {error}
          </p>
          {type === 'file' && value instanceof File && (
            <button
              type="button"
              onClick={() => onChange(null)}
              className="text-xs font-medium text-[var(--error)] hover:underline"
            >
              Remove this file and keep the rest of the form
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default FormField;
